import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import '../styles/pricing.css';

const Pricing = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.apiUrl}/public-modules.php`, { timeout: 15000 });
      const list = response.data.modules || [];
      setModules(list);
      setSelected(list.map((m) => m.id));
      setError(null);
    } catch (err) {
      console.error('Failed to load modules:', err);
      setError('Unable to load app pricing. Showing defaults.');
      const fallback = getFallbackModules();
      setModules(fallback);
      setSelected(fallback.map((m) => m.id));
    } finally {
      setLoading(false);
    }
  };

  const getFallbackModules = () => [
    { id: 1, module_code: 'pos', module_name: 'POS', icon: '🛒', price_monthly: 1499, description: 'Checkout, receipts, barcode scanning.' },
    { id: 2, module_code: 'inventory', module_name: 'Inventory', icon: '📦', price_monthly: 1499, description: 'Products, stock, suppliers, low-stock alerts.' },
    { id: 3, module_code: 'finance', module_name: 'Finance', icon: '💰', price_monthly: 1999, description: 'Debts, expenses, and financial overview.' },
    { id: 4, module_code: 'crm', module_name: 'CRM', icon: '👥', price_monthly: 1299, description: 'Customers, history, and debtors.' }
  ];

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const total = modules
    .filter((m) => selected.includes(m.id))
    .reduce((sum, m) => sum + parseFloat(m.price_monthly || 0), 0);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0).replace('KES', 'KSh');

  if (loading) {
    return (
      <div className="pricing-loading">
        <div className="spinner"></div>
        <p>Loading apps…</p>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <h1>Build Your Harvyq ERP</h1>
        <p>Choose the apps you need. Your monthly cost is the sum of selected modules.</p>
      </div>

      {error && (
        <div className="pricing-error">
          <p>{error}</p>
          <button type="button" onClick={loadModules}>Retry</button>
        </div>
      )}

      <div className="pricing-total-bar">
        <div>
          <span className="pricing-total-label">Estimated monthly total</span>
          <strong className="pricing-total-amount">{formatCurrency(total)}</strong>
        </div>
        <span>{selected.length} app{selected.length === 1 ? '' : 's'} selected</span>
      </div>

      <div className="pricing-grid">
        {modules.map((mod) => {
          const isOn = selected.includes(mod.id);
          return (
            <div
              key={mod.id}
              className={`pricing-card ${isOn ? 'selected' : ''}`}
              onClick={() => toggle(mod.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggle(mod.id)}
            >
              <div className="pricing-card-top">
                <span className="pricing-icon">{mod.icon || '📦'}</span>
                <input type="checkbox" checked={isOn} readOnly />
              </div>
              <h3>{mod.module_name}</h3>
              <p className="pricing-desc">{mod.description}</p>
              <div className="price">
                <span className="currency">KSh</span>
                <span className="amount">{Number(mod.price_monthly).toLocaleString()}</span>
                <span className="period">/month</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="pricing-cta">
        <p>Start a free trial, then activate the apps that fit your business.</p>
        <Link to="/register" className="btn-primary">
          Start Free Trial
        </Link>
        <Link to="/login" className="btn-secondary" style={{ marginLeft: '0.75rem' }}>
          Already have an account?
        </Link>
      </div>
    </div>
  );
};

export default Pricing;
